/* ==========================================================================
   MERCH EXPRESS — LOGO TRANSITION ANIMATION
   --------------------------------------------------------------------------
   Drives the hero's "budget promotion.ca → merch express" card. Ported from
   ani.html; the keyframes all live in me-rebrand.css, this file only:

     1. splits the old brand name into per-letter spans with a stagger,
     2. measures the FLIP offset + scale for the solo "budget" beat,
     3. adds .is-playing once the card scrolls into view.

   The hidden "before" frame is gated on .is-armed, which the inline snippet
   in the section adds during parse. If this file errors or never loads the
   card just renders its finished state — see the CSS for the full contract.
   ========================================================================== */
(function () {
  'use strict';

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* stagger for the per-letter entry, in seconds — mirrors the .is-playing
     timeline in me-rebrand.css, where the card lands at ~0.66s */
  var CHAR_START = 0.62;
  var CHAR_STEP  = 0.045;

  function splitLetters(card) {
    var targets = card.querySelectorAll('[data-me-swap-split]');
    Array.prototype.forEach.call(targets, function (el) {
      if (el.dataset.meSwapSplitDone === '1') return;
      el.dataset.meSwapSplitDone = '1';

      var text = el.textContent;
      el.textContent = '';

      Array.prototype.forEach.call(text, function (chr, i) {
        var span = document.createElement('span');
        span.className = 'me-swap__ch';
        // preserve runs of spaces, which would otherwise collapse
        span.textContent = chr === ' ' ? ' ' : chr;
        span.style.animationDelay = (CHAR_START + i * CHAR_STEP).toFixed(3) + 's';
        el.appendChild(span);
      });
    });
  }

  /* How far must the budget lockup travel to sit centred in the row, and how
     much can it scale up while it is alone on stage? offsetLeft/offsetWidth
     are layout values, so they stay correct under the transforms already
     sitting on the card and the lockup — unlike getBoundingClientRect(). That
     is why this never has to strip .is-playing to take a clean reading:
     doing so would re-trigger the whole timeline on every resize. */
  function measure(card) {
    var budget = card.querySelector('.me-swap__budget');
    var row    = card.querySelector('.me-swap__row');
    if (!budget || !row) return;

    var bw = budget.offsetWidth;
    var rw = row.offsetWidth;
    var dx = (row.offsetLeft + rw / 2) - (budget.offsetLeft + bw / 2);
    var hero = Math.min(1.34, (rw * 0.86) / Math.max(bw, 1));

    card.style.setProperty('--me-swap-dx', dx.toFixed(2) + 'px');
    card.style.setProperty('--me-swap-hero', hero.toFixed(3));
  }

  /* full length of the .is-playing timeline in me-rebrand.css, in ms */
  var RUN_MS = 3900;

  function play(card) {
    measure(card);
    void card.offsetWidth;              // force reflow so the anims restart
    card.dataset.meSwapState = 'playing';
    card.classList.add('is-playing');
    window.setTimeout(function () { card.dataset.meSwapState = 'done'; }, RUN_MS);
  }

  function initCard(card) {
    if (!card || card.dataset.meSwapReady === '1') return;
    card.dataset.meSwapReady = '1';

    // reduced motion: leave it unarmed and it renders the finished frame
    if (REDUCED) {
      card.classList.remove('is-armed');
      return;
    }

    // the inline snippet normally arms it during parse; cover the Theme
    // Editor's section re-render, where that snippet has already run
    card.classList.add('is-armed');
    splitLetters(card);

    card.dataset.meSwapState = 'idle';

    var started = false;
    var start = function () {
      if (started) return;             // fonts.ready settles once, but guard anyway
      started = true;

      if (!('IntersectionObserver' in window)) { play(card); return; }

      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          io.disconnect();
          play(card);
        });
      }, { threshold: 0.35 });

      io.observe(card);
    };

    /* Webfonts change the measured width of "budget", so wait them out — but
       the card is already armed (invisible) at this point, so never let a
       stalled font load leave the hero with a hole in it. */
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(start).catch(start);
      window.setTimeout(start, 2500);
    } else {
      start();
    }

    /* Re-measure on resize, but never mid-flight: changing --me-swap-dx while
       meSwapSettle is running would make the lockup jump. Once the timeline is
       done the vars only matter for a Theme Editor replay. */
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        if (card.dataset.meSwapState === 'playing') return;
        measure(card);
      }, 150);
    });
  }

  function initAll(scope) {
    var root = scope || document;
    Array.prototype.forEach.call(root.querySelectorAll('[data-me-swap]'), initCard);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // re-init when the section is reloaded inside the Theme Editor
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();
